// Premium: conta e pagamento por Pix.
//
// O fluxo e o mesmo do 38 Brasil: entra ou cria a conta, gera o Pix, paga, e o
// jogo confirma -- pelo webhook do banco ou pelo botao "ja paguei". A mesma
// conta libera no computador e no celular.
//
// Nada aqui finge que o pagamento deu certo. Sem servidor ou sem credenciais
// configuradas, a tela diz isso com todas as letras -- um jogo que "libera" o
// premium sozinho em erro de rede e um jogo que perde dinheiro em silencio.

import React, { useEffect, useState } from 'react';
import {
  readAccount, writeAccount, createAccount, loginAccount, fetchAccount,
  createUnlockPayment, checkUnlockPayment,
} from './api.js';
import { PREMIUM } from './premium-config.js';

export function Premium({ onBack, onPremiumChange, account, setAccount }) {
  const [tab, setTab] = useState(account ? 'pagar' : 'conta');
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState(account?.email || '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [payment, setPayment] = useState(null);
  const [copied, setCopied] = useState(false);

  const premium = !!account?.premium;

  // Se a conta ja e premium, confirma com o servidor ao abrir a tela.
  useEffect(() => {
    if (!account?.token) return;
    fetchAccount(account.token)
      .then((me) => {
        const next = { ...account, ...me };
        setAccount(writeAccount(next));
        onPremiumChange(!!me.premium);
      })
      .catch(() => { /* offline: mantem o que ja estava salvo */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doAccount = async () => {
    setBusy(true); setMsg('');
    try {
      const fn = mode === 'criar' ? createAccount : loginAccount;
      const data = await fn(email.trim().toLowerCase(), password);
      const next = { email: email.trim().toLowerCase(), token: data.token, premium: !!data.premium, ceo: !!data.ceo };
      setAccount(writeAccount(next));
      onPremiumChange(next.premium);
      setTab('pagar');
      setMsg(next.premium
        ? 'Conta ok. O premium ja esta ativo nela.'
        : mode === 'criar' ? 'Conta criada. Agora e so pagar o Pix uma vez.' : 'Login ok.');
    } catch (err) {
      setMsg(err.message);
    } finally { setBusy(false); }
  };

  const startPix = async () => {
    if (!account?.token) { setTab('conta'); setMsg('Entre ou crie a conta antes de pagar.'); return; }
    setBusy(true); setMsg('');
    try {
      const pay = await createUnlockPayment(account.token);
      if (pay.premium) { finish(); return; }
      setPayment(pay);
    } catch (err) {
      setMsg(err.message);
    } finally { setBusy(false); }
  };

  const checkPix = async () => {
    if (!payment?.paymentId) return;
    setBusy(true); setMsg('');
    try {
      const res = await checkUnlockPayment(account.token, payment.paymentId);
      if (res.premium) finish();
      else setMsg('Ainda nao identificamos o pagamento. Se ja pagou, espere alguns segundos e verifique de novo.');
    } catch (err) {
      setMsg(err.message);
    } finally { setBusy(false); }
  };

  const finish = () => {
    const next = { ...account, premium: true };
    setAccount(writeAccount(next));
    onPremiumChange(true);
    setPayment(null);
    setMsg('Premium liberado. A NBA esta aberta.');
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(payment.copyPaste);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setMsg('Nao consegui copiar. Selecione o codigo e copie na mao.');
    }
  };

  return (
    <>
      <div className="topbar">
        <div className="inner">
          <div className="row" style={{ gap: 8 }}>
            <button className="ghost sm" onClick={onBack}>voltar</button>
            <div className="brand">82 <b>Basquete</b></div>
          </div>
          <div className="meta">{PREMIUM.name}</div>
        </div>
      </div>

      <div className="wrap">
        {premium ? (
          <div className="pitch center">
            <div style={{ fontSize: 34 }}>🏀</div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4 }}>Premium ativo</div>
            <div className="muted small" style={{ marginTop: 4 }}>
              A NBA esta liberada nesta conta ({account.email}). Vale no computador e no
              celular: e so entrar com a mesma conta.
            </div>
            <button className="primary" style={{ marginTop: 14 }} onClick={onBack}>voltar ao jogo</button>
          </div>
        ) : (
          <>
            <div className="pitch">
              <div className="row spread" style={{ marginBottom: 6 }}>
                <span className="tag">opcional</span>
                <div className="price">
                  <span className="v">{PREMIUM.priceLabel}</span>
                  <span className="u">{PREMIUM.kind}</span>
                </div>
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-.02em' }}>
                {PREMIUM.name}
              </div>
              <div className="muted small" style={{ marginTop: 3 }}>{PREMIUM.pitch}</div>
              <div className="feature-grid">
                {PREMIUM.features.map((f) => <span key={f}>{f}</span>)}
              </div>
              <div className="dim small">
                Pagamento unico por Pix. Sem mensalidade, sem renovacao.
              </div>
            </div>

            <div className="row wrap" style={{ gap: 6, marginBottom: 12 }}>
              {[['conta', account ? 'Conta' : 'Entrar / Criar'], ['pagar', 'Pagar com Pix']].map(([id, label]) => (
                <button key={id} className={`sm ${tab === id ? 'primary' : 'ghost'}`} onClick={() => setTab(id)}>
                  {label}
                </button>
              ))}
            </div>

            {tab === 'conta' && (
              <div className="card">
                <div className="row wrap" style={{ gap: 6, marginBottom: 12 }}>
                  {[['login', 'Ja tenho conta'], ['criar', 'Criar conta']].map(([id, label]) => (
                    <button key={id} className={`sm ${mode === id ? 'primary' : 'ghost'}`} onClick={() => setMode(id)}>
                      {label}
                    </button>
                  ))}
                </div>
                <h3>E-mail</h3>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@email.com" autoComplete="email"
                />
                <h3 style={{ marginTop: 12 }}>Senha</h3>
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="pelo menos 6 caracteres"
                  autoComplete={mode === 'criar' ? 'new-password' : 'current-password'}
                />
                <button
                  className="primary" style={{ width: '100%', marginTop: 14 }}
                  disabled={busy || !email.includes('@') || password.length < 6}
                  onClick={doAccount}
                >
                  {busy ? 'aguarde...' : mode === 'criar' ? 'Criar conta' : 'Entrar'}
                </button>
                <div className="dim small" style={{ marginTop: 10 }}>
                  A conta serve para o premium valer em qualquer aparelho. O jogo em si
                  roda sem conta nenhuma.
                </div>
              </div>
            )}

            {tab === 'pagar' && (
              <div className="card">
                {!payment ? (
                  <>
                    <h3>Pagamento</h3>
                    <div className="muted small" style={{ marginBottom: 12 }}>
                      {account
                        ? <>Entrando como <b>{account.email}</b>. Gere o Pix, pague, e o premium
                          abre nessa conta.</>
                        : 'Entre ou crie a conta na aba ao lado antes de gerar o Pix.'}
                    </div>
                    <button
                      className="primary" style={{ width: '100%' }}
                      disabled={busy || !account}
                      onClick={startPix}
                    >
                      {busy ? 'gerando...' : `Gerar Pix de ${PREMIUM.priceLabel}`}
                    </button>
                  </>
                ) : (
                  <>
                    <h3>Pague o Pix de {PREMIUM.priceLabel}</h3>
                    {payment.qrImage && (
                      <div className="pix-box">
                        <img src={payment.qrImage} alt="QR Code do Pix" />
                      </div>
                    )}
                    <div className="dim small" style={{ margin: '10px 0 6px' }}>
                      Ou copie o codigo e cole no aplicativo do banco:
                    </div>
                    <div className="pix-code">{payment.copyPaste}</div>
                    <div className="row wrap" style={{ gap: 8, marginTop: 12 }}>
                      <button className="ghost" onClick={copy}>
                        {copied ? 'copiado!' : 'copiar codigo'}
                      </button>
                      <button className="primary" disabled={busy} onClick={checkPix}>
                        {busy ? 'verificando...' : 'ja paguei'}
                      </button>
                    </div>
                    <div className="dim small" style={{ marginTop: 10 }}>
                      Assim que o banco confirmar, o premium abre sozinho. O botao acima
                      so adianta a verificacao.
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {msg && (
          <div className="card tight small" style={{ borderLeft: '3px solid var(--accent)' }}>
            {msg}
          </div>
        )}
        <div style={{ height: 30 }} />
      </div>
    </>
  );
}
